<template>
  <div class="gs-tracking-step">
    <section class="gs-wizard-card gs-tracking-step__summary">
      <div class="gs-tracking-step__head">
        <div>
          <h2>Tracking defaults and overrides</h2>
          <p>Manage checkout tracking pixels, conversion scripts, and API events without mixing them into fulfillment.</p>
        </div>
        <label class="gs-tracking-step__apply">
          <span>Apply as default to all price points</span>
          <gd-toggle aria-label="Apply tracking defaults to all price points" :value="applyTrackingDefault" @change="toggleApplyTrackingDefault" />
        </label>
      </div>

      <article class="gs-tracking-step__card">
        <div>
          <h3>Add Default Checkout Page Tracking Code</h3>
          <p>Scripts here run on the checkout page before a buyer completes the order.</p>
          <span>{{ checkoutTrackingSummary }}</span>
        </div>
        <gs-button type="button" variant="outline" size="sm" class="gs-tracking-step__card-action" @click="openCheckoutTrackingDrawer">Edit</gs-button>
      </article>

      <article class="gs-tracking-step__card">
        <div>
          <h3>Add Conversion Tracking Code</h3>
          <p>Conversion scripts fire after purchase. Keep price points on the default unless an offer needs its own code.</p>
        </div>
        <gs-button type="button" variant="outline" size="sm" class="gs-tracking-step__card-action" @click="openDefaultConversionDrawer">Edit default</gs-button>
      </article>

      <div class="gs-tracking-step__price-points">
        <price-point-groups :price-points="pricePoints" v-slot="{ pricePoint, index }">
        <article
          :key="pricePoint.id || index"
          class="gs-tracking-step__row"
          :class="{ 'is-open': expandedTrackingIndex === index }"
        >
          <button type="button" class="gs-tracking-step__row-head" @click="toggleTrackingAccordion(index)">
            <span>
              <strong>{{ pricePoint.name }}</strong>
              <em>{{ trackingSummary(pricePoint) }}</em>
            </span>
            <span class="gs-tracking-step__pill" :class="{ 'is-on': pricePoint.advanced_tracking_code_enabled }">
              {{ pricePoint.advanced_tracking_code_enabled ? 'Tracking code' : 'No code' }}
            </span>
            <span class="gs-tracking-step__pill" :class="{ 'is-on': pricePoint.facebook_conversion_api_enabled }">
              {{ pricePoint.facebook_conversion_api_enabled ? 'Conversion API' : 'API off' }}
            </span>
            <gs-icon :name="expandedTrackingIndex === index ? 'chevron-up' : 'chevron-down'" size="18"></gs-icon>
          </button>
          <div v-if="expandedTrackingIndex === index" class="gs-tracking-step__row-body">
            <div class="gs-tracking-step__detail-grid">
              <div>
                <small>Conversion code</small>
                <strong>{{ pricePoint.advanced_tracking_code_enabled ? 'Enabled' : 'Disabled' }}</strong>
              </div>
              <div>
                <small>Facebook Conversion API</small>
                <strong>{{ pricePoint.facebook_conversion_api_enabled ? 'Enabled' : 'Disabled' }}</strong>
              </div>
              <div>
                <small>Pixel</small>
                <strong>{{ pixelLabel(pricePoint.pixel_id) }}</strong>
              </div>
            </div>
            <gs-button type="button" variant="primary" size="sm" @click="openPricePointTrackingDrawer(index)">
              Edit tracking
            </gs-button>
          </div>
        </article>
        </price-point-groups>
      </div>

      <article class="gs-tracking-step__card">
        <div>
          <h3>Advanced Facebook Tracking Conversion API</h3>
          <p>Send server-side purchase events through a connected Facebook pixel.</p>
        </div>
        <gs-button type="button" variant="outline" size="sm" class="gs-tracking-step__card-action" @click="openDefaultApiDrawer">Edit default</gs-button>
      </article>
    </section>

    <gs-drawer
      :value="showTrackingDrawer && !showPixelEditor"
      @input="showTrackingDrawer = $event"
      size="xl"
      :title="drawerTitle"
      :subtitle="drawerDescription"
      @close="closeTrackingDrawer"
    >
      <div class="gs-tracking-step__drawer">
        <template v-if="drawerMode === 'checkout'">
          <label class="gs-tracking-step__field">
            <span>Checkout page tracking code</span>
            <text-area
              v-model="localModel.checkout_page_options.tracking_codes"
              value="localModel.checkout_page_options.tracking_codes"
              placeholder="Paste checkout page pixels, analytics, or custom scripts..."
            />
          </label>
        </template>

        <template v-else-if="editingPricePoint">
          <div class="gs-tracking-step__setting">
            <div>
              <strong>Conversion tracking code</strong>
              <span>Add scripts or pixels that fire after this price point is purchased.</span>
            </div>
            <gd-toggle aria-label="Enable conversion tracking code" :value="editingPricePoint.advanced_tracking_code_enabled" @change="toggleAdvancedTracking(editingIndex)" />
          </div>

          <label v-if="editingPricePoint.advanced_tracking_code_enabled" class="gs-tracking-step__field">
            <span>Tracking code</span>
            <text-area
              v-model="editingPricePoint.advanced_tracking_code_details.tracking_script"
              value="editingPricePoint.advanced_tracking_code_details.tracking_script"
              placeholder="Paste your conversion tracking code here..."
            />
          </label>

          <div class="gs-tracking-step__setting">
            <div>
              <strong>Advanced Facebook Tracking Conversion API</strong>
              <span>Send purchase events through a connected Facebook pixel.</span>
            </div>
            <gd-toggle aria-label="Enable Advanced Facebook Tracking Conversion API" :value="editingPricePoint.facebook_conversion_api_enabled" @change="toggleFacebookConversion(editingIndex)" />
          </div>

          <div v-if="editingPricePoint.facebook_conversion_api_enabled" class="gs-tracking-step__field">
            <div class="gs-tracking-step__pixel-heading">
              <span>Facebook pixel</span>
              <gs-button type="button" variant="outline" size="sm" @click="showPixelEditor = true">+ Add new Facebook pixel</gs-button>
            </div>
            <gs-name-select v-model="editingPricePoint.pixel_id" size="full" :options="facebookConversionListOptions" />
            <p class="gs-tracking-step__pixel-help">Choose a saved Facebook pixel or add one here. You can also manage your pixels in Vendor Settings → Facebook Conversion Pixels.</p>
            <p v-if="pixelSavedMessage" class="gs-tracking-step__pixel-saved" role="status">{{ pixelSavedMessage }}</p>
          </div>
        </template>
      </div>
    </gs-drawer>
    <facebook-pixel-editor v-if="showPixelEditor" @close="showPixelEditor = false" @saved="pixelSaved" />
  </div>
</template>

<script>
import FacebookPixelEditor from '@/components/FacebookPixelEditor.vue';
import PricePointGroups from '../components/PricePointGroups.vue';

export default {
  name: 'TrackingStep',
  components: {
    PricePointGroups,
    FacebookPixelEditor,
  },
  props: {
    model: {
      required: true,
    },
    facebookCoversionList: {
      required: true,
    },
  },
  data() {
    return {
      localModel: this.model,
      facebook_conversion_list: [...(this.facebookCoversionList || [])],
      applyTrackingDefault: false,
      expandedTrackingIndex: 0,
      editingIndex: null,
      drawerMode: '',
      showTrackingDrawer: false,
      showPixelEditor: false,
      pixelSavedMessage: '',
    };
  },
  computed: {
    pricePoints() {
      return this.localModel.pricing_points || [];
    },
    editingPricePoint() {
      return this.editingIndex === null ? null : this.pricePoints[this.editingIndex];
    },
    checkoutTrackingSummary() {
      return this.localModel.checkout_page_options && this.localModel.checkout_page_options.tracking_codes
        ? 'Checkout tracking code configured'
        : 'No checkout page pixels added yet';
    },
    drawerEyebrow() {
      if (this.drawerMode === 'checkout') {
        return 'Checkout page';
      }
      return this.applyTrackingDefault ? 'Default tracking rule' : 'Price point tracking';
    },
    drawerTitle() {
      if (this.drawerMode === 'checkout') {
        return 'Default checkout page tracking code';
      }
      return this.editingPricePoint ? this.editingPricePoint.name : 'Tracking rule';
    },
    drawerDescription() {
      if (this.drawerMode === 'checkout') {
        return 'Use this for pixels or scripts that should load on the checkout page itself.';
      }
      return 'Use conversion scripts and API events for what happens after a purchase is completed.';
    },
    facebookConversionListOptions() {
      return [
        { value: 0, text: this.$t('frontend_groovesell_edit_product_step7_select_pixel') },
        ...this.facebook_conversion_list.map((pixel) => ({
          value: pixel.id,
          text: pixel.name,
        })),
      ];
    },
  },
  watch: {
    facebookCoversionList(list) { this.facebook_conversion_list = [...(list || [])]; },
    localModel: {
      deep: true,
      handler() {
        this.$emit('updateModel', this.localModel);
      },
    },
  },
  mounted() {
    this.ensureTrackingShapes();
  },
  methods: {
    pixelSaved(pixel) {
      this.facebook_conversion_list = this.facebook_conversion_list.filter(item => String(item.id) !== String(pixel.id)).concat(pixel);
      this.$emit('updateFacebookPixels', this.facebook_conversion_list);
      if (this.editingPricePoint) this.$set(this.editingPricePoint, 'pixel_id', pixel.id);
      this.pixelSavedMessage = `${pixel.name} saved and selected. Save your product to apply this tracking setting.`;
      this.showPixelEditor = false;
    },
    validate() {
      this.$emit('on-validate', true);
      return true;
    },
    ensureTrackingShapes() {
      if (!this.localModel.checkout_page_options) {
        this.$set(this.localModel, 'checkout_page_options', { tracking_codes: '' });
      }
      if (typeof this.localModel.checkout_page_options.tracking_codes === 'undefined') {
        this.$set(this.localModel.checkout_page_options, 'tracking_codes', '');
      }
      this.pricePoints.forEach((pricePoint) => this.ensurePricePointTracking(pricePoint));
    },
    ensurePricePointTracking(pricePoint) {
      if (!pricePoint.advanced_tracking_code_details) {
        this.$set(pricePoint, 'advanced_tracking_code_details', { tracking_script: '' });
      }
      if (typeof pricePoint.advanced_tracking_code_enabled === 'undefined') {
        this.$set(pricePoint, 'advanced_tracking_code_enabled', 0);
      }
      if (typeof pricePoint.facebook_conversion_api_enabled === 'undefined') {
        this.$set(pricePoint, 'facebook_conversion_api_enabled', 0);
      }
      if (typeof pricePoint.pixel_id === 'undefined') {
        this.$set(pricePoint, 'pixel_id', 0);
      }
    },
    toggleTrackingAccordion(index) {
      this.expandedTrackingIndex = this.expandedTrackingIndex === index ? null : index;
    },
    openCheckoutTrackingDrawer() {
      this.drawerMode = 'checkout';
      this.editingIndex = null;
      this.showTrackingDrawer = true;
    },
    openDefaultConversionDrawer() {
      this.openPricePointTrackingDrawer(0);
      this.applyTrackingDefault = true;
    },
    openDefaultApiDrawer() {
      this.openPricePointTrackingDrawer(0);
      this.applyTrackingDefault = true;
    },
    openPricePointTrackingDrawer(index) {
      this.pixelSavedMessage = '';
      this.drawerMode = 'price-point';
      this.editingIndex = index;
      this.ensurePricePointTracking(this.pricePoints[index]);
      this.showTrackingDrawer = true;
    },
    closeTrackingDrawer() {
      if (this.applyTrackingDefault && this.editingPricePoint) {
        this.applyCurrentTrackingToAll();
      }
      this.showTrackingDrawer = false;
      this.drawerMode = '';
      this.editingIndex = null;
    },
    toggleApplyTrackingDefault() {
      this.applyTrackingDefault = !this.applyTrackingDefault;
      if (this.applyTrackingDefault) {
        this.applyCurrentTrackingToAll();
      }
    },
    applyCurrentTrackingToAll() {
      const source = this.editingPricePoint || this.pricePoints[0];
      if (!source) {
        return;
      }
      this.ensurePricePointTracking(source);
      this.pricePoints.forEach((pricePoint) => {
        this.ensurePricePointTracking(pricePoint);
        this.$set(pricePoint, 'advanced_tracking_code_enabled', source.advanced_tracking_code_enabled);
        this.$set(pricePoint, 'facebook_conversion_api_enabled', source.facebook_conversion_api_enabled);
        this.$set(pricePoint, 'pixel_id', source.pixel_id);
        this.$set(
          pricePoint,
          'advanced_tracking_code_details',
          JSON.parse(JSON.stringify(source.advanced_tracking_code_details || { tracking_script: '' }))
        );
      });
    },
    toggleAdvancedTracking(index) {
      const pricePoint = this.pricePoints[index];
      this.ensurePricePointTracking(pricePoint);
      this.$set(pricePoint, 'advanced_tracking_code_enabled', pricePoint.advanced_tracking_code_enabled ? 0 : 1);
    },
    toggleFacebookConversion(index) {
      const pricePoint = this.pricePoints[index];
      this.ensurePricePointTracking(pricePoint);
      this.$set(pricePoint, 'facebook_conversion_api_enabled', pricePoint.facebook_conversion_api_enabled ? 0 : 1);
      if (!pricePoint.facebook_conversion_api_enabled) {
        this.$set(pricePoint, 'pixel_id', 0);
      }
    },
    trackingSummary(pricePoint) {
      const parts = [];
      parts.push(pricePoint.advanced_tracking_code_enabled ? 'Conversion code configured' : 'No conversion code');
      parts.push(pricePoint.facebook_conversion_api_enabled ? `Pixel: ${this.pixelLabel(pricePoint.pixel_id)}` : 'Conversion API off');
      return parts.join(' • ');
    },
    pixelLabel(pixelId) {
      const pixel = this.facebook_conversion_list.find((item) => String(item.id) === String(pixelId));
      return pixel ? pixel.name : 'No pixel selected';
    },
  },
};
</script>

<style>
.gs-tracking-step {
  max-width: 820px;
  margin: 0 auto;
}


.gs-tracking-step__head {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.gs-tracking-step__head h2 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gs-tracking-step__head p,
.gs-tracking-step__card p,
.gs-tracking-step__row-head em,
.gs-tracking-step__setting span {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.45;
}

.gs-tracking-step__apply {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  align-self: flex-start;
  margin: 0;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
}

.gs-tracking-step__card,
.gs-tracking-step__row {
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
}

.gs-tracking-step__card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
  padding: 16px 18px;
}

.gs-tracking-step__card h3 {
  margin: 0 0 6px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.3;
}

.gs-tracking-step__card span {
  display: inline-flex;
  margin-top: 10px;
  padding: 6px 10px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  background: var(--gs-surface-subtle);
  border-radius: var(--gs-radius-sm);
}

.gs-tracking-step__card-action {
  min-width: 112px;
  white-space: nowrap;
}

.gs-tracking-step__price-points {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
}

.gs-tracking-step__row.is-open {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow), var(--gs-shadow-sm);
}

.gs-tracking-step__row-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto 24px;
  gap: 12px;
  align-items: center;
  width: 100%;
  min-height: 62px;
  padding: 0 16px;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-tracking-step__row-head strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-tracking-step__row-head em {
  display: block;
  margin-top: 3px;
  font-style: normal;
}

.gs-tracking-step__pill {
  padding: 7px 10px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 700;
  background: var(--gs-surface-subtle);
  border-radius: var(--gs-radius-sm);
}

.gs-tracking-step__pill.is-on {
  color: var(--gs-success);
  background: var(--gs-success-soft);
}

.gs-tracking-step__row-body {
  padding: 0 16px 16px;
  border-top: 1px solid var(--gs-border);
}

.gs-tracking-step__detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 14px 0;
}

.gs-tracking-step__detail-grid div {
  min-width: 0;
  padding: 12px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-tracking-step__detail-grid small {
  display: block;
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.gs-tracking-step__detail-grid strong {
  display: block;
  min-width: 0;
  overflow: hidden;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-tracking-step__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 700;
}

.gs-tracking-step__field textarea,
.gs-tracking-step__field .gd-textarea {
  min-height: 180px !important;
  font-size: 15px !important;
}

.gs-tracking-step__setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
  padding: 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-tracking-step__setting strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-tracking-step__pixel-heading { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.gs-tracking-step__pixel-help { margin:0; color:var(--gs-muted-foreground); font-size:13px; font-weight:400; line-height:1.5; }
.gs-tracking-step__pixel-saved { margin:4px 0 0; color:var(--gs-success); font-size:13px; font-weight:500; }

@media (max-width: 720px) {
  .gs-tracking-step__head,
  .gs-tracking-step__card,
  .gs-tracking-step__setting {
    flex-direction: column;
    align-items: stretch;
  }

  .gs-tracking-step__row-head,
  .gs-tracking-step__detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
